// Rebuilds the Deployment chart in the browser from the per-day series the
// Calculate job stores (compartment_reporting_views "deployment_daily_<X>").
// Port of compartment_report_calc_service/report_generation/deployment_chart.py
// generate_buckets() + build_deployment_chart(): every bar there is a plain sum
// of rows whose date falls in [bucket.start, bucket.end), so summing the days
// of each bar reproduces it exactly (to the cent - only the float summation
// order differs). Keep in sync with that module if its bucketing ever changes.

const MS_DAY = 86400000;

const parseIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
const toIso = (t) => new Date(t).toISOString().slice(0, 10);

function periodAnchor(t, frequency) {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  if (frequency === 'month') return Date.UTC(y, m, 1);
  if (frequency === 'quarter') return Date.UTC(y, Math.floor(m / 3) * 3, 1);
  if (frequency === 'year') return Date.UTC(y, 0, 1);
  throw new Error(`Unsupported frequency: ${frequency}`);
}

function nextPeriod(t, frequency) {
  const d = new Date(t);
  const step = { month: 1, quarter: 3, year: 12 }[frequency];
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + step, 1);
}

function isoWeek(t) {
  // ISO 8601: week of the Thursday in the same Monday-Sunday week.
  const d = new Date(t);
  const weekday = (d.getUTCDay() + 6) % 7;
  const thursday = t + (3 - weekday) * MS_DAY;
  const isoYear = new Date(thursday).getUTCFullYear();
  const week = Math.floor((thursday - Date.UTC(isoYear, 0, 1)) / MS_DAY / 7) + 1;
  return [isoYear, week];
}

function bucketLabel(t, frequency) {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  if (frequency === 'month') return `${y}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  if (frequency === 'quarter') return `${y}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
  if (frequency === 'year') return String(y);
  if (frequency === 'week') {
    const [isoYear, week] = isoWeek(t);
    return `${isoYear}-W${String(week).padStart(2, '0')}`;
  }
  if (frequency === 'day') return toIso(t);
  throw new Error(`Unsupported frequency: ${frequency}`);
}

// Half-open [start, end) buckets, calendar-anchored, first/last clipped to the
// requested window - same rules as deployment_chart.generate_buckets().
export function generateBuckets(startIso, endIso, frequency, maxBuckets = 2000) {
  const start = parseIso(startIso);
  const end = parseIso(endIso);
  if (start > end) return [];

  const edges = [];
  if (frequency === 'day') {
    for (let t = start; ; t += MS_DAY) {
      edges.push(t);
      if (t > end) break;
    }
  } else if (frequency === 'week') {
    const weekday = (new Date(start).getUTCDay() + 6) % 7;
    for (let t = start - weekday * MS_DAY; ; t += 7 * MS_DAY) {
      edges.push(t);
      if (t > end) break;
    }
  } else {
    for (let t = periodAnchor(start, frequency); ; t = nextPeriod(t, frequency)) {
      edges.push(t);
      if (t > end) break;
    }
  }

  if (edges.length - 1 > maxBuckets) {
    throw new Error(`Date range too wide for the selected frequency (max ${maxBuckets} buckets).`);
  }
  const labels = edges.slice(0, -1).map((t) => bucketLabel(t, frequency));
  edges[0] = Math.max(edges[0], start);
  edges[edges.length - 1] = Math.min(edges[edges.length - 1], end + MS_DAY);
  return labels.map((label, i) => ({ start: toIso(edges[i]), end: toIso(edges[i + 1]), label }));
}

// Kahan-compensated running sum, matching pandas' compensated groupby sum as
// closely as a different addition order allows.
function sumRange(values, from, to) {
  let sum = 0;
  let compensation = 0;
  for (let i = from; i < to; i += 1) {
    const y = values[i] - compensation;
    const t = sum + y;
    compensation = t - sum - y;
    sum = t;
  }
  return sum;
}

const SERIES = ['invested', 'principal_returned', 'interest_returned'];

// Same return shape as GET /api/deployment-chart (build_deployment_chart).
// `daily` = the stored deployment_daily view; startIso/endIso optional.
export function buildDeploymentChart(daily, frequency, startIso = null, endIso = null) {
  const frequencies = daily.frequencies || ['day', 'week', 'month', 'quarter', 'year'];
  if (!frequencies.includes(frequency)) {
    throw new Error(`Invalid frequency. Must be one of: ${frequencies.join(', ')}.`);
  }
  if (startIso && endIso && startIso > endIso) {
    throw new Error('start_date must be on or before end_date.');
  }
  const scope = daily.platform == null ? 'fund' : 'platform';
  const start = startIso || (daily.window && daily.window.start);
  const end = endIso || (daily.window && daily.window.end);
  if (!start || !end) {
    return { frequency, scope, platform: daily.platform ?? null, window: null, buckets: [] };
  }

  const bucketsMeta = generateBuckets(start, end, frequency, daily.max_buckets || 2000);
  const dayIndex = new Map(daily.days.map((d, i) => [d, i]));
  const firstDay = daily.days.length ? parseIso(daily.days[0]) : null;

  // Index range of stored days inside [bucket.start, bucket.end); days outside
  // the stored extent have no dated rows at all, so they contribute 0.
  const indexOf = (iso) => {
    if (dayIndex.has(iso)) return dayIndex.get(iso);
    if (firstDay === null) return 0;
    return parseIso(iso) < firstDay ? 0 : daily.days.length;
  };

  const buckets = bucketsMeta.map((meta) => {
    const from = indexOf(meta.start);
    const to = indexOf(meta.end);
    const bucket = { ...meta };
    SERIES.forEach((series) => {
      bucket[series] = to > from ? sumRange(daily[series], from, to) : 0;
    });
    return bucket;
  });

  return { frequency, scope, platform: daily.platform ?? null, window: { start, end }, buckets };
}
